import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  DESTINATIONS,
  type DestinationId,
  destinationFor,
} from "@/config/travelerNav";

const BAR_ORDER: DestinationId[] = [
  "home",
  "trips",
  "card",
  "health",
  "account",
];
const BAR = BAR_ORDER.map((id) => DESTINATIONS.find((d) => d.id === id)!);
import { useI18nStore } from "../../stores/i18nStore";

/**
 * The traveller's navigation on a phone: the five destinations in a floating
 * bar at the bottom, the card raised in the middle (UX_CONCEPT.md §4). The
 * sidebar lists the card fourth; here it keeps the centre, where the thumb is.
 *
 * It replaces the drawer behind the header's menu button, which put every
 * destination two taps away on the device the PWA is mostly used on. Hidden
 * from `lg` up, where the sidebar takes over.
 */
const BottomTabBar: React.FC = () => {
  const { t } = useI18nStore();
  const { pathname } = useLocation();
  const current = destinationFor(pathname);

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-3 z-40 lg:hidden"
      style={{ bottom: "calc(12px + env(safe-area-inset-bottom))" }}
    >
      <ul className="grid h-[68px] grid-cols-5 items-center rounded-3xl bg-white shadow-[0_12px_30px_-10px_rgba(17,22,58,0.30)] ring-1 ring-ink/5">
        {BAR.map((destination) => {
          const Icon = destination.icon;
          const active = current?.id === destination.id;
          const label = destination.label(t);

          if (destination.id === "card") {
            return (
              <li key={destination.id} className="flex justify-center">
                <Link
                  to={destination.path}
                  aria-label={label}
                  aria-current={active ? "page" : undefined}
                  className={`-mt-6 flex h-14 w-14 items-center justify-center rounded-[20px] bg-primary-600 text-white shadow-[0_10px_22px_-8px_rgba(51,36,214,0.7)] ${
                    active ? "ring-4 ring-primary-100" : ""
                  }`}
                >
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </Link>
              </li>
            );
          }

          return (
            <li key={destination.id} className="flex justify-center">
              <Link
                to={destination.path}
                aria-current={active ? "page" : undefined}
                className={`flex min-h-[48px] min-w-[56px] flex-col items-center justify-center gap-1 text-[11px] ${
                  active
                    ? "font-bold text-primary-600"
                    : "font-semibold text-muted-foreground"
                }`}
              >
                <Icon className="h-[22px] w-[22px]" aria-hidden="true" />
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomTabBar;
